import { equipmentPrettyType as prettyType } from 'Utils/filters';
import { EventEmitter } from '@angular/core';
import template from './eventsPanelSettingsModal.component.html';

/* @ngInject */
function eventsPanelSettingsModal(
  $uibModalInstance,
  $scope,
  growl,
  hotkeys,
  EquipmentInfoService,
  EventInfoService,
  EventsPanelService,
  equipment,
  initialFilters,
  requestee,
  settings,
  UserInfoService
) {
  const vm = this;
  const availableEquipmentTypes = new Set();
  const availableEquipment = new Map();
  const availableEventsMap = new Map();
  const eventsByEQType = new Map();

  let initialSettings = {};

  vm.loading = true;

  vm.tempSettings = {};

  vm.typeFilter = prettyType;
  vm.selected = () => EventsPanelService.hasFilters(vm.filters);
  vm.pending = changesArePending;
  vm.close = close;
  vm.dismiss = dismiss;
  vm.notifyChange = new EventEmitter();
  vm.restoreDefaults = restoreDefaults;
  vm.notifyAcknowledgeFormChange = new EventEmitter();

  vm.userIsSuper = UserInfoService.isCurrentUserSuperAdmin();
  vm.userOnlyAdmin = UserInfoService.isCurrentUserExactlyAdmin();

  vm.eventsFormOptions = {
    title: null,
    buildFromSchema: true,
    hideAdvanced: true,
  };

  vm.eventsAcknowledgeFormOptions = {
    title: '',
    buildFromSchema: true,
    hideAdvanced: true,
  };

  vm.eventFiltersFormSchema = {
    components: [
      {
        type: 'select',
        label: 'Equipment',
        key: 'equipment',
        description: 'Show only selected equipment',
        multiple: true,
        dataSrc: 'json',
        data: {
          json: [],
        },
        template: '<span class="pull-left">{{ item.name }}<br/><small>{{ item.equipmentType }}</small></span>',
        valueProperty: 'id',
        idPath: 'id',
        customOptions: {
          searchResultLimit: 10,
          classNames: {
            button: 'choices__button pull-right',
          },
        },
      },
      {
        type: 'select',
        label: 'Event Names',
        key: 'eventNames',
        description: 'Show only selected event names',
        multiple: true,
        data: {
          values: [],
        },
        template: '<span class="pull-left">{{ item.label }}<br/><small>{{ item.value }}</small></span>',
        valueProperty: 'value',
        idPath: 'value',
        searchFields: ['label', 'value'],
        customOptions: {
          searchResultLimit: 10,
          classNames: {
            button: 'choices__button pull-right',
          },
        },
      },
    ],
  };

  hotkeys.bindTo($scope).add({
    combo: 'mod+enter',
    callback: vm.close,
  });

  vm.$onInit = async () => {

    // Current
    vm.initialFilters = _.defaults(initialFilters, EventInfoService.getDefaultFilters());
    // New, pending
    vm.filters = {
      ..._.cloneDeep(vm.initialFilters),
    };
    // Available
    vm.filterOptions = EventInfoService.getDefaultFilters();

    initialSettings = EventsPanelService.normalizeSettings(settings);
    vm.settings = _.cloneDeep(initialSettings);

    _.forEach(await EquipmentInfoService.getEquipmentById(equipment), ({ id, name, equipmentType }) => {
      availableEquipment.set(id, { id, name, equipmentType, label: name });
      availableEquipmentTypes.add(equipmentType);
    });

    const eventPanelExclusionSet = new Set(EventInfoService.eventsConfig.eventPanelExclusions);
    const restrictedAvailableEvents = new Set(vm.filters.availableEvents);
    _.forEach(EventInfoService.eventsConfig.definitions, ({ eventName, title }) => {
      const eqType = eventName.split('/')[0];
      if (
        !eventPanelExclusionSet.has(eventName) &&
        availableEquipmentTypes.has(eqType) &&
        (restrictedAvailableEvents.size ? restrictedAvailableEvents.has(eventName) : true)
      ) {
        const evt = { value: eventName, label: title };
        availableEventsMap.set(eventName, evt);
        eventsByEQType.set(eqType, (eventsByEQType.get(eqType) || new Map()).set(eventName, evt));
      }
    });
    // Filtering options
    vm.filterOptions.equipment = [...availableEquipment.values()];
    vm.filterOptions.equipmentTypes = [...availableEquipmentTypes.keys()];
    updateEventOptions(vm.filters);
    _.set(vm.eventFiltersFormSchema, 'components[0].data.json', vm.filterOptions.equipment);
    vm.loading = false;
    // Digest here due to async (or modal will appear to load very slowly)
    $scope.$digest();
  };

  vm.updateEvents = (update) => {
    const { model } = update;
    $scope.$apply(() => {
      updateEventOptions(model);
      angular.copy(model.equipment, vm.filters.equipment);
      angular.copy(model.eventNames, vm.filters.eventNames);
      vm.maxQueryUpdated(model);
    });
  };

  vm.onSettingsChange = (value, property) => {
    $scope.$apply(() => {
      _.set(vm, property, value);
    });
  };

  vm.maxQueryUpdated = (model = vm.filters) => {
    /*
      ? If the user deletes the input field, the HTMLElement/Angular behavior will be to assign the model to `null`
      We need the value to be `undefined` instead because `null` doesn't apply function parameter defaulting,
      causing the API to return 0 days of events instead of defaulting to 90.
    */
    if (model.maxQueryDateRange) {
      vm.filters.maxQueryDateRange = model.maxQueryDateRange;
    } else {
      delete vm.filters.maxQueryDateRange;
    }
    triggerNotifyChange();
  };

  function updateEventOptions(model) {
    const eventOptions = new Map();
    if (_.isEmpty(model.equipment)) {
      availableEventsMap.forEach((val, key) => eventOptions.set(key, val));
    } else {
      const selectedEqTypes = _.reduce(
        model.equipment,
        (acc, id) => {
          if (availableEquipment.has(id)) {
            acc.add(availableEquipment.get(id).equipmentType);
          }
          return acc;
        },
        new Set()
      );
      selectedEqTypes.forEach((type) => {
        if (eventsByEQType.has(type)) {
          eventsByEQType.get(type).forEach((val, key) => eventOptions.set(key, val));
        }
      });
    }

    vm.filterOptions.eventNames = [...eventOptions.values()];
    // merge existing schema into new object to change references and trigger change detection
    // then set the values array explicitly to not carry over any existing values
    vm.eventFiltersFormSchema = _.set(
      _.merge({}, vm.eventFiltersFormSchema),
      'components[1].data.values',
      vm.filterOptions.eventNames
    );

    vm.eventAcknowledgeFormSchema = _.set(
      _.merge({}, vm.eventAcknowledgeFormSchema),
      'components[0].data.values',
      model.eventNames?.length > 0 ? model.eventNames : vm.filterOptions.eventNames
    );

    const keepers = _.filter(model.eventNames, evt => eventOptions.has(evt));
    model.eventNames.length = 0;
    model.eventNames.push(...keepers);
  }

  function triggerNotifyChange() {
    $scope.$evalAsync(() => {
      vm.notifyChange.emit({
        model: vm.filters,
      });
    });
  }

  vm.selectAll = (equipmentType) => {
    const { filters, filterOptions } = vm;
    const selectedEquipment =
      equipmentType === 'None'
        ? []
        : _.union(filters.equipment, _.map(_.filter(filterOptions.equipment, { equipmentType }), 'id'));
    vm.filters.equipment.length = 0;
    vm.filters.equipment.push(...selectedEquipment);
    updateEventOptions(vm.filters);
    triggerNotifyChange();
  };

  vm.clear = () => {
    const { filters } = vm;
    // Clear list filter types
    _.forEach([filters.equipment, filters.eventNames], (filter) => {
      filter.length = 0;
    });
    filters.hideAcknowledgedEvents = false;
    filters.showOnlyFlaggedEvents = false;
    // Unassign other filter types
    delete filters.maxQueryDateRange;
    triggerNotifyChange();
  };

  vm.apply = (form) => {
    if (form.$invalid) { return }

    const filters = getRawFilters();
    filters.flagged = filters.showOnlyFlaggedEvents ? true : undefined;

    // Only set acknowledged filter if hideAcknowledgedEvents is true
    filters.acknowledged = filters.hideAcknowledgedEvents ? false : undefined;

    close(filters, vm.settings);
  };

  vm.save = async (form) => {
    if (form.$invalid) { return }

    const filters = getRawFilters();
    filters.flagged = filters.showOnlyFlaggedEvents ? true : undefined;

    // Only set acknowledged filter if hideAcknowledgedEvents is true
    filters.acknowledged = filters.hideAcknowledgedEvents ? false : undefined;

    try {
      await EventsPanelService.setFilter(requestee, filters, true);
      vm.settings = await EventsPanelService.setSettings(requestee, vm.settings, true);

      successToast(true);
      close(filters, vm.settings);
    } catch {
      growl.error('An error occurred while updating your filters. Please try again.', { title: 'Oh no!' });
    }
  };

  function close(filters, settingChanges, options = {}) {
    if (vm.tutorial) {
      const TTL = 5000;
      _.debounce(vm.tutorial.destroy, TTL)();
    }

    $uibModalInstance.close({
      filters,
      settings: settingChanges,
      ...options,
    });
  }

  function restoreDefaults() {
    close(undefined, undefined, { restoredDefaults: true });
  }

  function dismiss() {
    $uibModalInstance.close();
  }


  /**
   * @param {boolean} filterWasSaved Whether to show 'save' or 'apply' toast messages
   * @returns {undefined}
   */
  function successToast(filterWasSaved) {
    growl.success(
      filterWasSaved ? '' : "The panel will now temporarily hide events that do not meet the your filter's criteria",
      { title: `Your filters have been ${filterWasSaved ? 'saved' : 'applied'}!`, ttl: 10000 }
    );
    if (filterWasSaved) {
      if (vm.tutorial) {
        vm.tutorial.destroy();
      }
    } else {
      vm.tutorial =
        vm.tutorial ||
        growl.info('Click the save button to have the panel always use your selected filters', {
          title: 'Want to keep these filters for next time?',
          ttl: 15000,
          disableCloseButton: false,
          disableCountDown: true,
        });
    }
  }

  function getRawFilters(filters = vm.filters) {
    const rawFilters = _.cloneDeep(filters);

    // Only include acknowledged filter if hideAcknowledgedEvents is true
    if (filters.hideAcknowledgedEvents === false) {
      delete rawFilters.acknowledged;
      delete rawFilters.hideAcknowledgedEvents;
    }

    if (filters.showOnlyFlaggedEvents === false) {
      delete rawFilters.showOnlyFlaggedEvents;
    }

    return rawFilters;
  }


  /**
   * @param {boolean} local Whether to compare changes to session or saved filters
   * @returns {boolean} Pending?
   */
  function changesArePending(local) {
    const savedFilter = local
      ? getRawFilters(vm.initialFilters)
      : EventsPanelService.getFilter(requestee) || EventInfoService.getDefaultFilters();

    const filterChanged = vm.filters && savedFilter ? !_.isEqual(savedFilter, getRawFilters(vm.filters)) : false;
    const settingsChanged = !_.isEqual(vm.settings, initialSettings);

    return filterChanged || settingsChanged;
  }
}

export default {
  name: 'eventsPanelSettingsModal',
  template,
  controller: eventsPanelSettingsModal,
  controllerAs: '$modal',
  bindings: {
    filters: '<',
  },
};
